import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  HostBinding,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { atxIsMobile$ } from '@epicuro-next/ataraxia/breakpoint';
import { cBasketCustomValidationTranslations } from '@epicuro-next/constants/features/localization';
import { selectBasketNotificationStyle } from '@epicuro-next/platform/configuration';
import {
  clearBasketNotification,
  selectActiveBasketDisplayedCurrency,
  selectActiveBasketId,
  selectActiveBasketQuantity,
  selectActiveBasketValue,
  selectBasketHeaderInfoRule,
  selectBasketLoading,
  selectBasketNotification,
  selectBasketValidationFunctionResults,
  selectOpenedBasketId,
  selectOperableBasketId,
} from '@epicuro-next/state/basket-state';
import { messageToTranslatedOrOriginal } from '@epicuro-next/utilities';
import { select, Store } from '@ngrx/store';
import { BasketNotificationStyle } from 'libs/constants/rule-engine/pulse-rules/src/lib/types/basket-notification-style';
import { combineLatest, Subject } from 'rxjs';
import { filter, map, take, withLatestFrom } from 'rxjs/operators';

import { NavigationService } from '../service/navigation.service';

const BASKET_ROUTE = '/basket';

@Component({
  selector: 'epc-basket-button',
  templateUrl: './basket-button.component.html',
  styleUrls: ['./basket-button.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class BasketButtonComponent implements OnInit, OnChanges, OnDestroy {
  public valueChange = false;
  public currentRoute: string;
  public basketLoading$ = this.store.pipe(select(selectBasketLoading));
  public basketQuantity$ = this.store.pipe(select(selectActiveBasketQuantity));
  public basketValue$ = this.store.pipe(select(selectActiveBasketValue));
  public basketCurrency$ = this.store.pipe(
    select(selectActiveBasketDisplayedCurrency),
  );

  public basketLink$ = this.store.pipe(
    select(selectActiveBasketId),
    map((basketId) => {
      return basketId ? [BASKET_ROUTE, basketId] : [BASKET_ROUTE];
    }),
  );

  // Can be opened by URL or active basket
  public viewInBasketLink$ = this.store.pipe(
    select(selectOperableBasketId),
    map((basketId) => {
      return basketId ? [BASKET_ROUTE, basketId] : [BASKET_ROUTE];
    }),
  );

  public isOnBasketRoute$ = this.navigationService.isRouteActive(BASKET_ROUTE);

  public showNavigateToBasketButton$ = combineLatest([
    this.isOnBasketRoute$,
    this.store.pipe(select(selectOpenedBasketId)),
    this.store.pipe(select(selectOperableBasketId)),
  ]).pipe(
    map(
      ([onBasketRoute, openedBasketId, operableBasketId]) =>
        !onBasketRoute || openedBasketId !== operableBasketId,
    ),
  );

  public basketHeaderInfo$ = this.store.pipe(
    select(selectBasketHeaderInfoRule),
  );

  @HostBinding('class')
  public get classList() {
    return this.filled ? 'filled' : '';
  }

  public isDesktop$ = atxIsMobile$.pipe(map((isMobile) => !isMobile));

  public basketNotification$ = combineLatest([
    this.store.pipe(select(selectBasketNotification)),
    this.isDesktop$,
  ]).pipe(
    filter(([, isDesktop]) => isDesktop),
    map(([basketNotification]) => basketNotification),
  );

  public readonly basketValidationError$ = this.store.pipe(
    select(selectBasketValidationFunctionResults),
    map((validations) =>
      validations?.map(
        messageToTranslatedOrOriginal(cBasketCustomValidationTranslations),
      ),
    ),
  );

  public readonly showBasketNotification$ = this.store
    .select(selectBasketNotificationStyle)
    .pipe(
      map((basketNotificationStyle) => {
        return basketNotificationStyle === BasketNotificationStyle.default;
      }),
    );

  private filled = false;
  private destroy$$ = new Subject<void>();

  constructor(
    private store: Store,
    private router: Router,
    private navigationService: NavigationService,
    private cdr: ChangeDetectorRef,
  ) {}

  public ngOnInit() {
    this.handleFilledStyle();
  }

  public ngOnChanges(changes: SimpleChanges) {
    if (
      changes.stats?.currentValue &&
      !changes.stats.isFirstChange() &&
      changes.stats.currentValue.requested.wsp !== 0 &&
      changes.stats.currentValue.requested.wsp !==
        changes.stats.previousValue?.requested.wsp
    ) {
      this.valueChange = true;
      this.cdr.markForCheck();
      this.queueResetAnimation();
    }
  }

  public ngOnDestroy() {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public trimWhitespace(value: string) {
    return value.replace('\xa0\xa0', '\xa0').trim();
  }

  private queueResetAnimation() {
    setTimeout(() => {
      this.valueChange = false;
      this.cdr.markForCheck();
    }, 2000);
  }

  public onClearNotification() {
    this.store.dispatch(clearBasketNotification());
  }

  public basketNavigation() {
    this.basketLink$
      .pipe(take(1), withLatestFrom(this.isOnBasketRoute$))
      .subscribe(([routeToBasket, isOnBasket]) => {
        this.router
          .navigate([{ outlets: { overlay: null } }], {
            skipLocationChange: !isOnBasket,
            queryParamsHandling: 'preserve',
          })
          .then(() => {
            this.router.navigate(routeToBasket);
          });
      });
  }

  private handleFilledStyle() {
    this.basketQuantity$.pipe(map((totals) => !!totals)).subscribe((filled) => {
      this.filled = filled;
      this.cdr.markForCheck();
    });
  }
}
